/**
 * Service Terceirizacao / OSE (Fase 10 — ADR-0025).
 *
 * Cobre Parceiros (CRUD), OSEs (CRUD + actions de transicao), Pontualidade.
 */

const PREFIXO_PARCEIRO = "/api/parceiros";
const PREFIXO_OSE = "/api/oses";

// ============================================================
// Parceiros
// ============================================================

export function listarParceiros(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.busca) usp.set("busca", params.busca);
  if (params.categoria) usp.set("categoria", params.categoria);
  if (params.ativo !== undefined) usp.set("ativo", String(params.ativo));
  const qs = usp.toString();
  return request(`${PREFIXO_PARCEIRO}${qs ? `?${qs}` : ""}`);
}

export function obterParceiro(request, id) {
  return request(`${PREFIXO_PARCEIRO}/${id}`);
}

export function criarParceiro(request, dados) {
  return request(PREFIXO_PARCEIRO, {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function atualizarParceiro(request, id, dados) {
  return request(`${PREFIXO_PARCEIRO}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

export function desativarParceiro(request, id) {
  return request(`${PREFIXO_PARCEIRO}/${id}`, { method: "DELETE" });
}

// ============================================================
// OSEs
// ============================================================

export function listarOses(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.situacao) usp.set("situacao", params.situacao);
  if (params.categoria) usp.set("categoria", params.categoria);
  if (params.parceiro) usp.set("parceiro", params.parceiro);
  if (params.atrasadas) usp.set("atrasadas", "true");
  const qs = usp.toString();
  return request(`${PREFIXO_OSE}${qs ? `?${qs}` : ""}`);
}

export function obterOse(request, id) {
  return request(`${PREFIXO_OSE}/${id}`);
}

export function criarOse(request, dados) {
  return request(PREFIXO_OSE, {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function atualizarOse(request, id, dados) {
  return request(`${PREFIXO_OSE}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

// Actions de transicao
export function enviarOse(request, id, { checklist } = {}) {
  return request(`${PREFIXO_OSE}/${id}/enviar`, {
    method: "POST",
    body: JSON.stringify({ checklist: checklist || {} }),
  });
}

export function registrarRecebimento(request, id, { descricao = "" } = {}) {
  return request(`${PREFIXO_OSE}/${id}/registrar-recebimento`, {
    method: "POST",
    body: JSON.stringify({ descricao }),
  });
}

export function registrarRetorno(request, id, { checklist } = {}) {
  return request(`${PREFIXO_OSE}/${id}/registrar-retorno`, {
    method: "POST",
    body: JSON.stringify({ checklist: checklist || {} }),
  });
}

export function aprovarOse(request, id, { observacao = "" } = {}) {
  return request(`${PREFIXO_OSE}/${id}/aprovar`, {
    method: "POST",
    body: JSON.stringify({ observacao }),
  });
}

export function rejeitarOse(request, id, { motivo }) {
  return request(`${PREFIXO_OSE}/${id}/rejeitar`, {
    method: "POST",
    body: JSON.stringify({ motivo }),
  });
}

export function cancelarOse(request, id, { motivo }) {
  return request(`${PREFIXO_OSE}/${id}/cancelar`, {
    method: "POST",
    body: JSON.stringify({ motivo }),
  });
}

export function comentarOse(request, id, { texto }) {
  return request(`${PREFIXO_OSE}/${id}/comentar`, {
    method: "POST",
    body: JSON.stringify({ texto }),
  });
}

// ============================================================
// Pontualidade
// ============================================================

export function obterPontualidade(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.janela_dias) usp.set("janela_dias", String(params.janela_dias));
  if (params.parceiro) usp.set("parceiro", params.parceiro);
  if (params.unidade) usp.set("unidade", params.unidade);
  const qs = usp.toString();
  return request(`/api/terceirizacao/pontualidade${qs ? `?${qs}` : ""}`);
}

// ============================================================
// Labels e tones (UI)
// ============================================================

export const SITUACAO_LABELS = {
  RASCUNHO: "Rascunho",
  ENVIADA: "Enviada",
  EM_PRODUCAO_TERCEIRO: "Em produção no terceiro",
  RETORNADA: "Retornada (aguarda CQ)",
  APROVADA: "Aprovada",
  REJEITADA: "Rejeitada",
  CANCELADA: "Cancelada",
};

export const SITUACAO_TONES = {
  RASCUNHO: "neutral",
  ENVIADA: "info",
  EM_PRODUCAO_TERCEIRO: "info",
  RETORNADA: "warning",
  APROVADA: "success",
  REJEITADA: "danger",
  CANCELADA: "neutral",
};

export const CATEGORIAS = [
  { value: "LAQUEACAO", label: "Laqueação" },
  { value: "PINTURA", label: "Pintura / Verniz" },
  { value: "VIDRARIA", label: "Vidraria" },
  { value: "METAIS", label: "Metais (puxadores, soldas)" },
  { value: "MARMORE", label: "Mármore / Granito" },
  { value: "OUTROS", label: "Outros" },
];

export const CATEGORIA_LABELS = Object.fromEntries(
  CATEGORIAS.map((c) => [c.value, c.label]),
);

export const SITUACOES_FILTRO = Object.entries(SITUACAO_LABELS).map(
  ([value, label]) => ({ value, label }),
);

export const JANELAS_PONTUALIDADE = [
  { value: 30, label: "Últimos 30 dias" },
  { value: 90, label: "Últimos 90 dias" },
  { value: 180, label: "Últimos 180 dias" },
  { value: 365, label: "Último ano" },
];

// ---------- Remessa de material (Bloco 8 do ADR-0061) ----------

/**
 * Material que saiu para o parceiro. Não sai do estoque: vai para o
 * depósito dele. O saldo da empresa só cai quando alguém quebra algo.
 */
export function listarRemessas(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.parceiro) usp.set("parceiro", params.parceiro);
  if (params.situacao) usp.set("situacao", params.situacao);
  if (params.emAberto) usp.set("em_aberto", "true");
  const qs = usp.toString();
  return request(`/api/remessas${qs ? `?${qs}` : ""}`);
}

export function criarRemessa(request, dados) {
  return request("/api/remessas/criar", {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function registrarRetornoRemessa(request, id, retornos) {
  return request(`/api/remessas/${id}/registrar-retorno`, {
    method: "POST",
    body: JSON.stringify({ retornos }),
  });
}

/** Se o parceiro sumir amanhã, quanto material nosso vai junto. */
export function obterMaterialEmTerceiros(request) {
  return request("/api/terceirizacao/material-em-terceiros");
}

export const SITUACAO_REMESSA_LABEL = {
  ENVIADA: "Enviada",
  RETORNADA_PARCIAL: "Retorno parcial",
  RETORNADA: "Retornada",
};

export const SITUACAO_REMESSA_TONE = {
  ENVIADA: "info",
  RETORNADA_PARCIAL: "warning",
  RETORNADA: "success",
};
